/**
 * Low-level xlsx ZIP surgery helpers (B1). PURE module: browser + Node safe.
 *
 * An xlsx file is a ZIP. We patch ONLY worksheet cell VALUES inside
 * `xl/worksheets/sheetN.xml` and leave `xl/media/*`, `xl/drawings/*`,
 * `xl/styles.xml`, theme and every other part byte-for-byte untouched. That is
 * what preserves the original BRITZMEDI form pixels: logo, signature/seal image,
 * fonts, borders, column widths, merged cells — all of it.
 *
 * We deliberately do NOT use exceljs to read the template: it throws
 * `Cannot read properties of undefined (reading 'anchors')` on these real forms
 * (embedded-image drawing anchor it cannot parse). Surgical XML editing sidesteps
 * the parser entirely.
 *
 * Scope of editing:
 *  - set a cell to an inline string  -> <c r=".." s=".." t="inlineStr"><is><t>..</t></is></c>
 *  - set a cell to a number          -> <c r=".." s=".."><v>..</v></c>
 *  - the existing style index (s="..") is ALWAYS preserved (keeps date/number
 *    format, borders, fonts of that cell).
 *  - a cell carrying a <f>formula</f> is converted to a literal value (formula
 *    dropped) so the output shows the injected value even in viewers that do not
 *    recalculate.
 */

import JSZip from "jszip";

/** XML-escape a text node value. */
export function xmlEscape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Excel serial number for a `YYYY-MM-DD` date (1900 system, matching the
 * template's date-formatted cells). Excel's off-by-one 1900 leap bug is included
 * via the +1 epoch offset (1899-12-30 baseline).
 */
export function dateToSerial(isoDate: string): number {
  const parts = isoDate.split("-").map((n) => parseInt(n, 10));
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const utc = Date.UTC(y, m - 1, d);
  const epoch = Date.UTC(1899, 11, 30); // 1899-12-30 → serial 0
  return Math.round((utc - epoch) / 86_400_000);
}

/** One cell edit. `kind` decides inlineStr vs numeric serialization. */
export type CellEdit =
  | { ref: string; kind: "string"; value: string }
  | { ref: string; kind: "number"; value: number }
  | { ref: string; kind: "blank" };

/** Column letters of a cell ref ("F40" -> "F"). */
function colOf(ref: string): string {
  return ref.replace(/\d+$/, "");
}
/** 1-based column index of letters ("A"->1, "B"->2, ... "AA"->27). */
function colIndex(letters: string): number {
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}
/** Row number of a cell ref ("F40" -> 40). */
function rowOf(ref: string): number {
  return parseInt(ref.replace(/^[A-Z]+/, ""), 10);
}

/** Pull the `s="N"` style index out of an existing `<c ...>` open tag. */
function styleAttr(openTag: string): string {
  const m = openTag.match(/\bs="(\d+)"/);
  return m ? ` s="${m[1]}"` : "";
}

/** Serialize a single edited `<c>` element, preserving the style index. */
function renderCell(ref: string, sAttr: string, edit: CellEdit): string {
  if (edit.kind === "blank") {
    return `<c r="${ref}"${sAttr}/>`;
  }
  if (edit.kind === "number") {
    return `<c r="${ref}"${sAttr}><v>${edit.value}</v></c>`;
  }
  return `<c r="${ref}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${xmlEscape(
    edit.value,
  )}</t></is></c>`;
}

/**
 * Apply a set of cell edits to one worksheet XML string.
 *
 * Strategy: for each target row we rebuild only the `<c>` elements we touch.
 * Cells that already exist are replaced in place (style preserved). Cells that
 * do NOT yet exist in the row are inserted in column order. Rows that do not yet
 * exist are appended to `<sheetData>` in row order.
 */
export function patchSheetXml(xml: string, edits: CellEdit[]): string {
  if (edits.length === 0) return xml;

  // group edits by row
  const byRow = new Map<number, Map<string, CellEdit>>();
  for (const e of edits) {
    const r = rowOf(e.ref);
    if (!byRow.has(r)) byRow.set(r, new Map());
    byRow.get(r)!.set(e.ref, e);
  }

  let out = xml;

  for (const [rowNum, rowEdits] of [...byRow.entries()].sort(
    (a, b) => a[0] - b[0],
  )) {
    const rowRe = new RegExp(
      `(<row\\b[^>]*\\br="${rowNum}"[^>]*>)([\\s\\S]*?)(</row>)`,
    );
    const match = out.match(rowRe);

    if (match) {
      const open = match[1] ?? "";
      const body = match[2] ?? "";
      const close = match[3] ?? "</row>";
      let newBody = body;
      const handled = new Set<string>();

      // replace existing cells
      const cellRe = /<c\b([^>]*?)(?:\/>|>[\s\S]*?<\/c>)/g;
      newBody = newBody.replace(cellRe, (cellXml, attrs: string) => {
        const ref = (attrs.match(/\br="([A-Z]+\d+)"/) || [])[1];
        if (ref && rowEdits.has(ref)) {
          handled.add(ref);
          return renderCell(ref, styleAttr(`<c${attrs}>`), rowEdits.get(ref)!);
        }
        return cellXml;
      });

      // insert cells that did not exist yet, in column order
      const toInsert = [...rowEdits.values()].filter(
        (e) => !handled.has(e.ref),
      );
      for (const e of toInsert) {
        const cellXml = renderCell(e.ref, "", e);
        newBody = insertCellInColumnOrder(newBody, e.ref, cellXml);
      }

      out = out.replace(rowRe, `${open}${newBody}${close}`);
    } else {
      // row does not exist — build it and splice into sheetData in row order
      const cells = [...rowEdits.values()]
        .sort((a, b) => colIndex(colOf(a.ref)) - colIndex(colOf(b.ref)))
        .map((e) => renderCell(e.ref, "", e))
        .join("");
      const rowXml = `<row r="${rowNum}">${cells}</row>`;
      out = insertRowInRowOrder(out, rowNum, rowXml);
    }
  }

  return out;
}

/** Insert a `<c>` element into a row body, keeping ascending column order. */
function insertCellInColumnOrder(
  body: string,
  ref: string,
  cellXml: string,
): string {
  const target = colIndex(colOf(ref));
  const cellRe = /<c\b[^>]*?(?:\/>|>[\s\S]*?<\/c>)/g;
  let m: RegExpExecArray | null;
  let insertAt = body.length;
  while ((m = cellRe.exec(body))) {
    const r = (m[0].match(/\br="([A-Z]+\d+)"/) || [])[1];
    if (r && colIndex(colOf(r)) > target) {
      insertAt = m.index;
      break;
    }
  }
  return body.slice(0, insertAt) + cellXml + body.slice(insertAt);
}

/** Splice a `<row>` element into `<sheetData>`, keeping ascending row order. */
function insertRowInRowOrder(
  xml: string,
  rowNum: number,
  rowXml: string,
): string {
  const rowRe = /<row\b[^>]*\br="(\d+)"[^>]*>[\s\S]*?<\/row>/g;
  let m: RegExpExecArray | null;
  let insertAt = -1;
  while ((m = rowRe.exec(xml))) {
    if (parseInt(m[1] ?? "0", 10) > rowNum) {
      insertAt = m.index;
      break;
    }
  }
  if (insertAt === -1) {
    // before </sheetData>
    return xml.replace("</sheetData>", `${rowXml}</sheetData>`);
  }
  return xml.slice(0, insertAt) + rowXml + xml.slice(insertAt);
}

/** Force a worksheet to be visible (used to unhide INVOICE-US / PACKING LIST). */
export function unhideSheet(workbookXml: string, sheetName: string): string {
  const re = new RegExp(
    `(<sheet\\b[^>]*\\bname="${sheetName}"[^>]*?)\\s*state="[^"]*"([^>]*/>)`,
  );
  return workbookXml.replace(re, "$1$2");
}

/** Set the active/first sheet tab index so the workbook opens on a visible sheet. */
export function setActiveTab(workbookXml: string, tabIndex: number): string {
  return workbookXml.replace(
    /(<workbookView\b[^>]*?)\s*activeTab="[^"]*"/,
    `$1 activeTab="${tabIndex}"`,
  );
}

/** Load an xlsx (bytes) into a JSZip instance. */
export async function loadXlsx(
  bytes: ArrayBuffer | Uint8Array,
): Promise<JSZip> {
  return JSZip.loadAsync(bytes);
}

/** Read a text part from the zip (throws if missing). */
export async function readPart(zip: JSZip, path: string): Promise<string> {
  const f = zip.file(path);
  if (!f) throw new Error(`[xlsx-zip] missing part: ${path}`);
  return f.async("string");
}

/** Write a text part back into the zip. */
export function writePart(zip: JSZip, path: string, content: string): void {
  zip.file(path, content);
}

/** Serialize the zip to xlsx bytes (ArrayBuffer). */
export async function toXlsxBytes(zip: JSZip): Promise<ArrayBuffer> {
  const u8 = await zip.generateAsync({
    type: "uint8array",
    compression: "DEFLATE",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  // Copy into a fresh ArrayBuffer (avoids SharedArrayBuffer typing; browser Blob
  // + Node Buffer both accept it).
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}
